import axios, { AxiosError } from 'axios';

import { getToken, removeToken } from '../../components/authentication/functions/authentication_management';

/**
 * Client for the Atlas backend (`getAtlasApiBaseUrl()`). Attaches the login token as a
 * bearer and ends the session once when the backend rejects that token with a 401.
 */

type UnauthorizedListener = () => void;

const HTTP_UNAUTHORIZED = 401;
const BEARER_PREFIX = 'Bearer ';

const unauthorizedListeners = new Set<UnauthorizedListener>();

export function subscribeAtlasUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

function currentAccessToken(): string | null {
  return getToken()?.access_token ?? null;
}

function bearerToken(authorization: unknown): string | null {
  if (typeof authorization !== 'string' || !authorization.startsWith(BEARER_PREFIX)) {
    return null;
  }
  return authorization.slice(BEARER_PREFIX.length);
}

/**
 * Clears the session when the rejected token is still the stored one. Later 401s for the
 * same token find it already cleared, so concurrent failures end the session once.
 */
function handleUnauthorized(sentToken: string | null): void {
  const rejectedTokenIsCurrent = sentToken !== null && sentToken === currentAccessToken();
  if (!rejectedTokenIsCurrent) {
    return;
  }

  removeToken();
  unauthorizedListeners.forEach((listener) => {
    listener();
  });
}

function headersToRecord(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) {
    return {};
  }
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...headers };
}

function findAuthorization(headers: Record<string, string>): string | undefined {
  const key = Object.keys(headers).find((name) => name.toLowerCase() === 'authorization');
  return key === undefined ? undefined : headers[key];
}

export const atlasBackendApi = axios.create();

atlasBackendApi.interceptors.request.use((config) => {
  const token = currentAccessToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `${BEARER_PREFIX}${token}`;
  }
  return config;
});

atlasBackendApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === HTTP_UNAUTHORIZED) {
      handleUnauthorized(bearerToken(error.config?.headers?.Authorization));
    }
    return Promise.reject(error);
  },
);

export async function atlasFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const headers = headersToRecord(init.headers);
  const token = currentAccessToken();
  if (token && findAuthorization(headers) === undefined) {
    headers.Authorization = `${BEARER_PREFIX}${token}`;
  }

  const response = await fetch(input, { ...init, headers });
  if (response.status === HTTP_UNAUTHORIZED) {
    handleUnauthorized(bearerToken(findAuthorization(headers)));
  }
  return response;
}
